import { useState, useEffect } from 'react';
import axios from 'axios';
import { Routing } from '../Routes';
import { useApi, Res } from '../../state/api';
import { Main } from '../Main/Main';
import { useTranslation } from '../../i18n/i18n';
import { LoaderPage } from './LoaderPage';
import { ErrorPage } from './ErrorPage';
import { ApiActionResponse } from '../../types';
import { useConfig } from '../../config';
import { Button } from '../Form/Button';
import { ContentCard } from '../ContentCard/ContentCard';
import { InputGrid } from '../Form/InputGrid';
import { Loader } from '../Loader/Loader';
import './CommunicationPage.css';

export const CommunicationPage = (): JSX.Element => {
	const t = useTranslation().communication;
	const { api } = useConfig();
	const { data: communications, error } = useApi(Res.communications, {});
	const { data: subIds, isLoading, mutate } = useApi(Res.subscriptions, {});
	const [checked, setChecked] = useState<number[]>([]);
	const [buttonText, setButtonText] = useState(t.saveChanges);
	const [message, setMessage] = useState('');
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (subIds) setChecked(subIds);
	}, [isLoading, subIds]);

	async function post(payload: number[]): Promise<{ data?: ApiActionResponse; error?: unknown }> {
		return axios.post<ApiActionResponse>(api + Res.subscriptions.uri({}), payload, { withCredentials: true });
	}

	if (error) return <ErrorPage />;
	if (!communications || !subIds) return <LoaderPage />;

	const handleOnChange = (id: number) => {
		if (checked.includes(id)) {
			setChecked(checked.filter((i) => i !== id));
		} else {
			setChecked([...checked, id]);
		}
	};
	const noChanges = subIds.length === checked.length && checked.every((item) => subIds.indexOf(item) > -1);

	async function handleSubmit() {
		const payload: number[] = checked;
		mutate(payload, false);
		setLoading(true);
		setMessage('');
		const res = await post(payload);
		if (res.data !== undefined) {
			setButtonText(t.saved);
			setMessage(t.changesSaved);
			setLoading(false);
			mutate();
		}
	}

	return (
		<Main>
			<div id="communication-page">
				<ContentCard title={t.subscriptionSettings}>
					<p>{t.handleCommunication}</p>
					<InputGrid>
						{communications.map((item) => (
							<label key={item.id} className="communication-item">
								<input
									type="checkbox"
									className="communication-item-checkbox"
									value={item.id}
									checked={checked.includes(item.id)}
									onChange={() => handleOnChange(item.id)}
									disabled={loading}
								/>
								<span className="communication-item-text">
									<strong>{item.name}</strong> - {item.description}
								</span>
							</label>
						))}
					</InputGrid>
					<p className="important-info">
						<span>{t.importantInformation}</span>
					</p>
					<div className="card-footer">
						{noChanges && message}
						{loading && <Loader size="icon" />}
						<Button disabled={noChanges || loading} onClick={handleSubmit}>
							{noChanges ? buttonText : t.saveChanges}
						</Button>
					</div>
				</ContentCard>
			</div>
		</Main>
	);
};

export const communicationRouting: Routing = {
	path(urls) {
		return urls.communication;
	},
	component: CommunicationPage,
	requiresAuth: true,
};
